/**
 * Social Login Buttons Component
 *
 * Provides Google and Apple login buttons that redirect to Cognito OAuth.
 * Automatically detects Apple devices and shows Apple Sign In first (Apple HIG best practice).
 */
import { useMemo } from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import {
  OAuthState,
  signInWithApple,
  signInWithGoogle,
} from '@splitfiesta/platform-web/auth/cognito';
import { FormButton } from '@splitfiesta/ui';

type EnabledProvider = 'google' | 'apple';

interface SocialLoginButtonsProps {
  /**
   * Variant to show different text for login vs register
   */
  variant?: 'login' | 'register';
  /**
   * Whether the buttons are disabled
   */
  disabled?: boolean;
  /**
   * Additional class names
   */
  className?: string;
  /**
   * Which providers to show (default: Google and Apple)
   */
  enabledProviders?: EnabledProvider[];
  /**
   * Optional OAuth state to preserve through OAuth flow (invite token, language)
   */
  oauthState?: OAuthState;
}

/**
 * Detect if the user is on an Apple device (iOS, iPadOS, macOS)
 */
function isAppleDevice(): boolean {
  if (typeof navigator === 'undefined') return false;

  const userAgent = navigator.userAgent.toLowerCase();
  const platform = (navigator.platform || '').toLowerCase();

  // Check for iOS/iPadOS
  const isIOS =
    /iphone|ipad|ipod/.test(userAgent) ||
    (platform === 'macintel' && navigator.maxTouchPoints > 1); // iPad with desktop mode

  // Check for macOS
  const isMacOS =
    platform.includes('mac') || /macintosh|mac os x/.test(userAgent);

  return isIOS || isMacOS;
}

type SocialProvider = 'google' | 'apple';

interface ProviderConfig {
  id: SocialProvider;
  onClick: () => void;
  Icon: React.ComponentType;
  testId: string;
}

export function SocialLoginButtons({
  variant = 'login',
  disabled = false,
  className = '',
  enabledProviders = ['google', 'apple'],
  oauthState,
}: SocialLoginButtonsProps) {
  const { t } = useTranslation();

  const translationKey =
    variant === 'login'
      ? 'auth:login.socialLogin'
      : 'auth:register.socialLogin';

  // Define providers with their configurations
  const providers: ProviderConfig[] = useMemo(() => {
    const allProviders: ProviderConfig[] = [
      {
        id: 'google',
        onClick: () => signInWithGoogle(oauthState),
        Icon: GoogleIcon,
        testId: 'social-login-google',
      },
      {
        id: 'apple',
        onClick: () => signInWithApple(oauthState),
        Icon: AppleIcon,
        testId: 'social-login-apple',
      },
    ];

    // Filter to only enabled providers
    const filtered = allProviders.filter(p => enabledProviders.includes(p.id));

    // On Apple devices, show Apple first (Apple HIG recommendation)
    if (isAppleDevice() && enabledProviders.includes('apple')) {
      const appleProvider = filtered.find(p => p.id === 'apple');
      if (appleProvider) {
        const otherProviders = filtered.filter(p => p.id !== 'apple');
        return [appleProvider, ...otherProviders];
      }
    }

    return filtered;
  }, [enabledProviders, oauthState]);

  return (
    <div className={`space-y-3 ${className}`}>
      {providers.map(({ id, onClick, Icon, testId }) => (
        <FormButton
          key={id}
          type="button"
          variant="outline"
          onClick={onClick}
          disabled={disabled}
          fullWidth
          icon={Icon}
          className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
          data-testid={testId}
        >
          {t(`${translationKey}.${id}`)}
        </FormButton>
      ))}
    </div>
  );
}

// SVG Icons for social providers
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={`${className} text-slate-900 dark:text-white`}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}
